/**
 * OV2 New Order multi-price cache — instant repeat configs + prefetched default price.
 */
import api from "@/lib/api";
import {
  fetchOrderCatalog,
  getDefaultCakeItemFromCatalog,
} from "../../hooks/useOrderCatalog";

const MAX_CACHE_ENTRIES = 50;
const priceCache = new Map();
const cacheOrder = [];

function packagingForCheeseCake(weightKg) {
  const w = parseFloat(weightKg) || 0.25;
  if (w <= 0.375) return "bento";
  if (w <= 0.875) return "half_kg";
  return "1kg";
}

function normalizePricingResponse(data, expandedItems) {
  return {
    ...data,
    items: (data.items || []).map((row, idx) => ({
      ...row,
      flavor: row.flavor || expandedItems[idx]?.flavor,
      order_type: row.order_type || expandedItems[idx]?.order_type,
      weight_kg: row.weight_kg ?? expandedItems[idx]?.weight_kg,
    })),
  };
}

function prepareItemsForMultiPrice(items) {
  return items.map((item) => ({
    ...item,
    decor_level:
      item.order_type === "Custom" || item.order_type === "Cupcake"
        ? item.decor_level
        : "Standard",
    packaging_type:
      item.order_type === "Cheese Cake"
        ? packagingForCheeseCake(item.weight_kg)
        : item.packaging_type || "half_kg",
  }));
}

export function buildMultiPriceCacheKey(expandedItems, discountPercent) {
  return JSON.stringify({
    items: expandedItems,
    discount_percent: discountPercent ?? 0,
  });
}

export function getCachedMultiPrice(key) {
  return priceCache.get(key) ?? null;
}

export function setCachedMultiPrice(key, data) {
  if (!key || !data) return;
  if (priceCache.has(key)) {
    priceCache.set(key, data);
    return;
  }
  priceCache.set(key, data);
  cacheOrder.push(key);
  while (cacheOrder.length > MAX_CACHE_ENTRIES) {
    const oldest = cacheOrder.shift();
    if (oldest) priceCache.delete(oldest);
  }
}

/** Warm default first-cake price after catalog prefetch (Layout / hover). */
export async function prefetchDefaultNewOrderPrice(discountPercent = 0) {
  try {
    const catalog = await fetchOrderCatalog();
    const defaultItem = getDefaultCakeItemFromCatalog(catalog.flavors);
    const expandedItems = prepareItemsForMultiPrice([defaultItem]);
    const key = buildMultiPriceCacheKey(expandedItems, discountPercent);
    if (priceCache.has(key)) return priceCache.get(key);

    const response = await api.post("/calculate-multi-price", {
      items: expandedItems,
      discount_percent: discountPercent,
    });
    const normalized = normalizePricingResponse(response.data, expandedItems);
    setCachedMultiPrice(key, normalized);
    return normalized;
  } catch (error) {
    console.error("Failed to prefetch default New Order price:", error);
    return null;
  }
}

export { prepareItemsForMultiPrice, normalizePricingResponse };
